import React, { Suspense } from 'react';
import { Navigate, Routes, Route } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Spin } from 'antd';
import type { RootState } from 'sama-shared';
import { UserType } from 'sama-shared';
import { useFinanceLiveSync } from '../../features/admin/hooks/useFinanceLiveSync';

const AdminHome = React.lazy(() => import('../../features/admin/views/AdminHome'));
const AdminPartnerView = React.lazy(() => import('../../features/admin/views/AdminPartner'));
const AdminPartnerDetail = React.lazy(
  () => import('../../features/admin/views/AdminPartnerDetail')
);
const AdminOrderPaymentView = React.lazy(
  () => import('../../features/admin/views/AdminOrderPayment')
);
const AdminFinancialReport = React.lazy(
  () => import('../../features/admin/views/AdminFinancialReport')
);
const AdminSubscriptionPlanView = React.lazy(
  () => import('../../features/admin/views/AdminSubscriptionPlan')
);
const AdminSubscriptionManager = React.lazy(
  () => import('../../features/admin/views/AdminSubscription')
);
const AdminInvoiceView = React.lazy(() => import('../../features/admin/views/AdminInvoice'));
const AdminIndustryView = React.lazy(() => import('../../features/admin/views/AdminIndustry'));
const AdminDeliveryProviderDetail = React.lazy(
  () => import('../../features/admin/views/AdminDeliveryProviderDetail')
);
const AdminDeliveryProviders = React.lazy(
  () => import('../../features/admin/views/AdminDeliveryProvider')
);
const AdminDeliveryFinancialReport = React.lazy(
  () => import('../../features/admin/views/AdminDeliveryFinancialReport')
);
const AdminPaymentMethods = React.lazy(
  () => import('../../features/admin/views/AdminPaymentMethod')
);
const AdminSettingsView = React.lazy(() => import('../../features/admin/views/AdminSetting'));
const AdminPartnerCollectSettlements = React.lazy(
  () => import('../../features/admin/views/AdminPartnerCollectSettlements')
);
const AdminOrderView = React.lazy(() => import('../../features/admin/views/AdminOrder'));
const AdminOrderDetail = React.lazy(() => import('../../features/admin/views/AdminOrderDetail'));
const AdminPartnerInvoices = React.lazy(
  () => import('../../features/admin/views/AdminPartnerInvoices')
);
const AdminPartnerInvoiceDetail = React.lazy(
  () => import('../../features/admin/views/AdminPartnerInvoiceDetail')
);

const AdminRouteFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spin size="large" />
  </div>
);

export const AdminRoute: React.FC = () => {
  const userType = useSelector((state: RootState) => state.auth.userType);
  useFinanceLiveSync(userType === UserType.admin);

  if (userType !== UserType.admin) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Suspense fallback={<AdminRouteFallback />}>
      <Routes>
        <Route index element={<AdminHome />} />
        <Route path="partners" element={<AdminPartnerView />} />
        <Route path="partners/:id" element={<AdminPartnerDetail />} />
        <Route path="orders" element={<AdminOrderView />} />
        <Route path="orders/:id" element={<AdminOrderDetail />} />
        <Route path="delivery-providers" element={<AdminDeliveryProviders />} />
        <Route path="payment-methods" element={<AdminPaymentMethods />} />
        <Route path="settings" element={<AdminSettingsView />} />
        <Route path="order-payments" element={<AdminOrderPaymentView />} />
        <Route path="financial-report" element={<AdminFinancialReport />} />
        <Route path="delivery-financial-report" element={<AdminDeliveryFinancialReport />} />
        <Route path="partner-settlements" element={<AdminPartnerCollectSettlements />} />
        <Route path="partner-invoices" element={<AdminPartnerInvoices />} />
        <Route path="partner-invoices/:id" element={<AdminPartnerInvoiceDetail />} />
        <Route path="subscription-plans" element={<AdminSubscriptionPlanView />} />
        <Route path="subscriptions" element={<AdminSubscriptionManager />} />
        <Route path="invoices" element={<AdminInvoiceView />} />
        <Route path="industries" element={<AdminIndustryView />} />
        <Route path="delivery-providers/:id" element={<AdminDeliveryProviderDetail />} />

        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </Suspense>
  );
};

export default AdminRoute;
